// /client/billing — read-only summary of every approved-or-later case for
// the signed-in client. State-driven copy on contract + payment rows;
// real DocuSign / Stripe wiring lands in later prompts. Pure read view —
// no mutations from this page.

import type { Href } from "expo-router";
import { router, useLocalSearchParams } from "expo-router";
import { useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Platform,
  Pressable,
  ScrollView,
  Text,
  useWindowDimensions,
  View,
  type ViewStyle,
} from "react-native";
import Svg, { Path } from "react-native-svg";

import {
  listBillingForClient,
  type BillingCase,
} from "../../../lib/clientBilling";
import { COLORS, FONTS } from "../../../lib/brand";
import { PANE_THEMES } from "../../../components/brand/paneThemes";
import { formatCents } from "../../../lib/caseApprovals";
import { formatDateWithYear } from "../../../lib/datetime";
import type { CompetitionStatus } from "../../../types/database";

const THEME = PANE_THEMES.Client;

const BLACK = THEME.pageBg;
const FOREST = THEME.sidebarBg;
const FOREST_DEEP = COLORS.FOREST_DEEP.hex;
const AMBER = THEME.accent;
const SAGE = COLORS.SAGE.hex;
const LINEN = THEME.textOnSurface;

const W = "rgba(255,255,255,1)";
const WS = "rgba(255,255,255,0.78)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const WX = "rgba(255,255,255,0.16)";
const BORDER = "rgba(255,255,255,0.07)";
const RED = THEME.destructive;
const RED_TINT = "rgba(220,80,80,0.14)";
const SAGE_TINT = "rgba(143,187,138,0.18)";

const PAGE_MAX_W = 1080;
const NARROW_BREAKPOINT = 600;

export default function ClientBillingScreen() {
  const { width } = useWindowDimensions();
  const isNarrow = width < NARROW_BREAKPOINT;
  const [cases, setCases] = useState<BillingCase[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Stripe Checkout success_url + cancel_url route back here with ?paid /
  // ?cancelled, used to surface a one-shot toast above the cards.
  const searchParams = useLocalSearchParams<{ paid?: string; cancelled?: string }>();
  const toast = useMemo<{ tone: "success" | "neutral"; message: string } | null>(() => {
    if (typeof searchParams.paid === "string" && searchParams.paid.length > 0) {
      return { tone: "success", message: "Payment received — your case is live!" };
    }
    if (typeof searchParams.cancelled === "string" && searchParams.cancelled.length > 0) {
      return {
        tone: "neutral",
        message: "Payment cancelled — no charge made. Click Pay now to try again.",
      };
    }
    return null;
  }, [searchParams.paid, searchParams.cancelled]);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    void listBillingForClient()
      .then((rows) => {
        if (!cancelled) setCases(rows);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Failed to load billing");
          setCases([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <ScrollView style={{ flex: 1, backgroundColor: BLACK }} contentContainerStyle={{ paddingBottom: 64 }}>
      <View
        style={{
          paddingHorizontal: isNarrow ? 16 : 32,
          paddingTop: 28,
          maxWidth: PAGE_MAX_W,
        }}
      >
        {/* Header */}
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <View style={{ width: 22, height: 2, backgroundColor: AMBER, opacity: 0.85 }} />
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 9.5,
              letterSpacing: 2,
              textTransform: "uppercase",
              color: AMBER,
            }}
          >
            Account · Billing
          </Text>
        </View>
        <Text
          style={{
            marginTop: 6,
            fontFamily: FONTS.display,
            fontSize: isNarrow ? 30 : 36,
            lineHeight: isNarrow ? 34 : 40,
            color: W,
          }}
        >
          Billing
        </Text>
        <Text
          style={{
            marginTop: 8,
            fontFamily: FONTS.body,
            fontSize: 13,
            lineHeight: 20,
            color: WM,
            maxWidth: 720,
          }}
        >
          Quotes, contracts, and payments for your cases.
        </Text>

        {/* Stripe Checkout return-trip toast (one-shot, no dismiss button —
            navigating elsewhere clears the query param). */}
        {toast ? (
          <View
            style={{
              marginTop: 18,
              padding: 12,
              borderRadius: 4,
              backgroundColor: toast.tone === "success" ? SAGE_TINT : "rgba(255,255,255,0.04)",
              borderWidth: 1,
              borderColor:
                toast.tone === "success" ? "rgba(143,187,138,0.45)" : "rgba(255,255,255,0.12)",
            }}
            accessibilityRole="alert"
          >
            <Text
              style={{
                fontFamily: FONTS.bodyMedium,
                fontSize: 12.5,
                color: toast.tone === "success" ? SAGE : WS,
              }}
            >
              {toast.message}
            </Text>
          </View>
        ) : null}

        {/* Error banner */}
        {error ? (
          <View
            style={{
              marginTop: 18,
              padding: 12,
              borderRadius: 4,
              backgroundColor: RED_TINT,
              borderWidth: 1,
              borderColor: "rgba(220,80,80,0.40)",
            }}
          >
            <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12.5, color: RED }}>
              {error}
            </Text>
          </View>
        ) : null}

        {/* Body */}
        {cases === null ? (
          <View style={{ paddingVertical: 40, alignItems: "center" }}>
            <ActivityIndicator color={AMBER} />
          </View>
        ) : cases.length === 0 ? (
          <BillingEmptyState />
        ) : (
          <>
            <View style={{ marginTop: 22, gap: 14 }}>
              {cases.map((c) => (
                <BillingCard key={c.competition_id} item={c} isNarrow={isNarrow} />
              ))}
            </View>
            <BillingFooterNote />
          </>
        )}
      </View>
    </ScrollView>
  );
}

/* ─────────────────────────  Empty state  ───────────────────────── */

function BillingEmptyState() {
  return (
    <View
      style={{
        marginTop: 28,
        padding: 28,
        borderRadius: 8,
        backgroundColor: FOREST_DEEP,
        borderWidth: 1,
        borderColor: BORDER,
        alignItems: "flex-start",
      }}
    >
      <View
        style={{
          width: 44,
          height: 44,
          borderRadius: 999,
          backgroundColor: "rgba(181,135,58,0.16)",
          borderWidth: 1,
          borderColor: "rgba(181,135,58,0.32)",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <IconReceipt color={AMBER} size={22} />
      </View>
      <Text
        style={{
          marginTop: 16,
          fontFamily: FONTS.displayMedium,
          fontSize: 22,
          color: LINEN,
        }}
      >
        No quotes yet
      </Text>
      <Text
        style={{
          marginTop: 6,
          fontFamily: FONTS.body,
          fontSize: 13,
          lineHeight: 20,
          color: WM,
          maxWidth: 520,
        }}
      >
        When Coravu approves your first case, your quote and billing details will live
        here. Submit a case for review from the dashboard to get started.
      </Text>
      <Pressable
        onPress={() => router.push("/client" as Href)}
        accessibilityRole="link"
        style={[
          {
            marginTop: 18,
            paddingHorizontal: 16,
            paddingVertical: 9,
            borderRadius: 3,
            backgroundColor: AMBER,
          } as ViewStyle,
          Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
        ]}
      >
        <Text
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 11,
            letterSpacing: 1.6,
            textTransform: "uppercase",
            color: FOREST,
          }}
        >
          View dashboard
        </Text>
      </Pressable>
    </View>
  );
}

/* ─────────────────────────  Footer note  ───────────────────────── */

function BillingFooterNote() {
  return (
    <Text
      style={{
        marginTop: 22,
        fontFamily: FONTS.body,
        fontSize: 11,
        lineHeight: 17,
        color: WL,
        maxWidth: 720,
      }}
    >
      Quotes include Stripe Tax — your final invoice may include sales tax based on your
      billing address. Coravu is not a tax advisor; contact your accountant for
      tax-specific questions.
    </Text>
  );
}

/* ─────────────────────────  Case card  ───────────────────────── */

function BillingCard({ item, isNarrow }: { item: BillingCase; isNarrow: boolean }) {
  const [expanded, setExpanded] = useState(false);

  const isRm = item.engagement_model === "rm";
  const isLic = item.engagement_model === "licensing";
  const engagementLabel = isLic ? "LIC" : isRm ? "RM" : "Hybrid";
  const q = item.quote;

  return (
    <View
      style={{
        backgroundColor: FOREST_DEEP,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: BORDER,
        overflow: "hidden",
      }}
    >
      {/* Card header */}
      <View
        style={{
          paddingHorizontal: isNarrow ? 14 : 20,
          paddingTop: 14,
          paddingBottom: 12,
          flexDirection: "row",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <Pressable
          accessibilityRole="link"
          onPress={() =>
            router.push(`/client/competition/${item.competition_id}/edit` as Href)
          }
          style={[
            { flex: 1, minWidth: 0 } as ViewStyle,
            Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
          ]}
        >
          <Text
            style={{
              fontFamily: FONTS.displayMedium,
              fontSize: 20,
              color: LINEN,
            }}
            numberOfLines={2}
          >
            {item.case_name}
          </Text>
        </Pressable>
        <View
          style={{
            flexDirection: "row",
            gap: 6,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <EngagementChip label={engagementLabel} />
          <StatusChip status={item.status} />
        </View>
      </View>

      {/* Divider */}
      <View style={{ height: 1, backgroundColor: BORDER }} />

      {/* Quote summary row */}
      <View style={{ paddingHorizontal: isNarrow ? 14 : 20, paddingVertical: 14 }}>
        <QuoteSummaryRow item={item} isNarrow={isNarrow} />

        {/* Expandable breakdown trigger */}
        {q ? (
          <Pressable
            onPress={() => setExpanded((v) => !v)}
            accessibilityRole="button"
            accessibilityLabel={expanded ? "Hide quote breakdown" : "Show quote breakdown"}
            style={[
              {
                marginTop: 12,
                flexDirection: "row",
                alignItems: "center",
                gap: 6,
                alignSelf: "flex-start",
              } as ViewStyle,
              Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
            ]}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 10.5,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                color: AMBER,
              }}
            >
              {expanded ? "Hide breakdown" : "Quote breakdown"}
            </Text>
            <Chevron open={expanded} color={AMBER} />
          </Pressable>
        ) : null}

        {expanded && q ? (
          <QuoteBreakdown
            item={item}
            isRm={isRm}
            isNarrow={isNarrow}
          />
        ) : null}
      </View>

      {/* Divider */}
      <View style={{ height: 1, backgroundColor: BORDER }} />

      {/* Contract row */}
      <ContractRow item={item} isNarrow={isNarrow} />

      {/* Divider */}
      <View style={{ height: 1, backgroundColor: BORDER }} />

      {/* Payment row */}
      <PaymentRow item={item} isNarrow={isNarrow} />

      {/* Final invoice (closeout) — only when one exists. Renders below
          the initial payment with status-driven CTAs + overdue styling. */}
      {item.final_invoice ? (
        <>
          <View style={{ height: 1, backgroundColor: BORDER }} />
          <FinalInvoiceRow item={item} isNarrow={isNarrow} />
        </>
      ) : null}
    </View>
  );
}

/* ─────────────────────────  Quote summary  ───────────────────────── */

function QuoteSummaryRow({ item, isNarrow }: { item: BillingCase; isNarrow: boolean }) {
  const q = item.quote;
  if (!q) {
    return (
      <Text style={{ fontFamily: FONTS.body, fontSize: 13, color: WM }}>
        No quote on file yet — Coravu will generate one with your approval.
      </Text>
    );
  }
  const tierLabel = item.tier ? `Tier ${item.tier}` : "Tier —";
  const scholarship = formatCents(q.scholarship_amount_cents);
  const initial = formatCents(q.initial_payment_cents);

  if (isNarrow) {
    return (
      <View style={{ gap: 6 }}>
        <SummaryLineStacked label="Tier" value={tierLabel.replace(/^Tier /, "")} prefix="Tier" />
        <SummaryLineStacked label="Scholarship" value={scholarship} />
        <SummaryLineStacked label="Initial payment due" value={initial} />
      </View>
    );
  }

  // Wide: separated by middots
  return (
    <View style={{ flexDirection: "row", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
      <SummaryInline label="Tier" value={item.tier ?? "—"} />
      <Dot />
      <SummaryInline label="Scholarship" value={scholarship} />
      <Dot />
      <SummaryInline label="Initial payment due" value={initial} />
    </View>
  );
}

function SummaryInline({ label, value }: { label: string; value: string }) {
  return (
    <View style={{ flexDirection: "row", alignItems: "baseline", gap: 6 }}>
      <Text
        style={{
          fontFamily: FONTS.body,
          fontSize: 11.5,
          color: WL,
          letterSpacing: 0.4,
          textTransform: "uppercase",
        }}
      >
        {label}
      </Text>
      <Text style={{ fontFamily: FONTS.bodySemiBold, fontSize: 14, color: LINEN }}>
        {value}
      </Text>
    </View>
  );
}

function SummaryLineStacked({
  label,
  value,
  prefix,
}: {
  label: string;
  value: string;
  prefix?: string;
}) {
  return (
    <View>
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 9.5,
          letterSpacing: 1.6,
          textTransform: "uppercase",
          color: WL,
        }}
      >
        {label}
      </Text>
      <Text
        style={{
          marginTop: 2,
          fontFamily: FONTS.bodySemiBold,
          fontSize: 15,
          color: LINEN,
        }}
      >
        {prefix ? `${prefix} ${value}` : value}
      </Text>
    </View>
  );
}

function Dot() {
  return <Text style={{ color: WL, fontSize: 12 }}>·</Text>;
}

/* ─────────────────────────  Quote breakdown  ───────────────────────── */

function QuoteBreakdown({
  item,
  isRm,
  isNarrow,
}: {
  item: BillingCase;
  isRm: boolean;
  isNarrow: boolean;
}) {
  const q = item.quote;
  if (!q) return null;

  const rows: Array<{ label: string; value: string }> = [
    { label: "Per-student rate", value: formatCents(q.per_student_rate_cents) },
    { label: "Per-submission rate", value: formatCents(q.per_submission_rate_cents) },
  ];
  if (isRm) {
    if (q.per_team_presentation_rate_cents != null) {
      rows.push({
        label: "Per-team-presentation rate",
        value: formatCents(q.per_team_presentation_rate_cents),
      });
    }
    if (q.flat_rm_fee_cents != null) {
      rows.push({ label: "Flat RM fee", value: formatCents(q.flat_rm_fee_cents) });
    }
  }
  if (q.manual_discount_percent > 0) {
    rows.push({
      label: "Manual discount",
      value: `${q.manual_discount_percent}%`,
    });
  }
  if (q.promo_code && (q.promo_discount_cents ?? 0) > 0) {
    rows.push({
      label: `Promo: ${q.promo_code}`,
      value: `−${formatCents(q.promo_discount_cents ?? 0)}`,
    });
  }

  return (
    <View
      style={{
        marginTop: 14,
        paddingTop: 12,
        borderTopWidth: 1,
        borderTopColor: BORDER,
      }}
    >
      {/* Acts as a description list. RN doesn't expose <dl>/<dt>/<dd>
          primitives, so each row pairs a label Text with a value Text
          in a flex container — accessibilityRole="list" on the outer
          View groups them for assistive tech. */}
      <View
        accessibilityRole="list"
        accessibilityLabel="Quote breakdown"
        style={{ gap: isNarrow ? 10 : 6 }}
      >
        {rows.map((r) => (
          <BreakdownRow key={r.label} label={r.label} value={r.value} isNarrow={isNarrow} />
        ))}
      </View>

      {q.notes_for_client ? (
        <View style={{ marginTop: 14 }}>
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 9.5,
              letterSpacing: 1.6,
              textTransform: "uppercase",
              color: WL,
            }}
          >
            Notes from Coravu
          </Text>
          <Text
            style={{
              marginTop: 4,
              fontFamily: FONTS.body,
              fontSize: 12.5,
              lineHeight: 19,
              color: WS,
            }}
          >
            {q.notes_for_client}
          </Text>
        </View>
      ) : null}
    </View>
  );
}

function BreakdownRow({
  label,
  value,
  isNarrow,
}: {
  label: string;
  value: string;
  isNarrow: boolean;
}) {
  if (isNarrow) {
    return (
      <View
        accessibilityLabel={`${label}: ${value}`}
        style={{ flexDirection: "column", gap: 2 }}
      >
        <Text
          style={{
            fontFamily: FONTS.bodyMedium,
            fontSize: 11,
            letterSpacing: 0.4,
            color: WL,
            textTransform: "uppercase",
          }}
        >
          {label}
        </Text>
        <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 13.5, color: LINEN }}>
          {value}
        </Text>
      </View>
    );
  }
  return (
    <View
      accessibilityLabel={`${label}: ${value}`}
      style={{ flexDirection: "row", justifyContent: "space-between", gap: 10 }}
    >
      <Text style={{ fontFamily: FONTS.body, fontSize: 12.5, color: WM }}>
        {label}
      </Text>
      <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 13, color: LINEN }}>
        {value}
      </Text>
    </View>
  );
}

/* ─────────────────────────  Contract row  ───────────────────────── */

function ContractRow({ item, isNarrow }: { item: BillingCase; isNarrow: boolean }) {
  // The visible state is driven by the contract row when present; falls
  // back to `competitions.status` copy for cases that haven't reached
  // the contract step yet.
  const k = item.contract;
  if (item.status === "denied" || item.status === "denied_terminal") {
    return <StatusInfoRow label="Contract" body="N/A" isNarrow={isNarrow} />;
  }

  // DocuSign sandbox app URL — clients without easy access to their
  // original DocuSign email can sign in to their DocuSign account to
  // find the envelope. Future polish wires a recipient-view magic-link
  // generated server-side. Sandbox vs. prod is a server-side concern.
  const docusignAppUrl = "https://apps-d.docusign.com/send/documents";

  if (k) {
    let body: string;
    let showOpenLink = false;
    switch (k.status) {
      case "sent":
        body = `DocuSign envelope sent ${formatRelative(k.sent_at)}. Check your email for the sign link.`;
        showOpenLink = true;
        break;
      case "viewed":
        body = "You've opened the contract — sign to proceed.";
        showOpenLink = true;
        break;
      case "signed":
        body = `Contract signed on ${formatDateWithYear(k.signed_at)}. Payment link arrives next.`;
        break;
      case "declined":
        body = k.declined_reason
          ? `You declined this contract: "${k.declined_reason}". Contact admin@coravu.com to revise.`
          : "You declined this contract. Contact admin@coravu.com to revise.";
        break;
      case "voided":
        body = "Contract voided. A new envelope will be issued shortly.";
        break;
      default:
        body = "Awaiting contract.";
    }
    return (
      <StatusInfoRow
        label="Contract"
        body={body}
        isNarrow={isNarrow}
        rhs={showOpenLink ? <OpenInDocuSignButton url={docusignAppUrl} /> : undefined}
      />
    );
  }

  // No contract row yet — fall back to case-status copy.
  let body: string;
  if (item.status === "approved") {
    body = "Awaiting contract — Coravu will send a DocuSign envelope shortly.";
  } else if (
    item.status === "contract_pending" ||
    item.status === "contract_signed" ||
    item.status === "payment_pending" ||
    item.status === "final_invoice_pending" ||
    item.status === "final_invoice_paid"
  ) {
    // Bare advance without a contract row is rare (the create-envelope
    // flow always inserts) but possible after a manual SQL fix-up.
    body = "Contract status pending — refresh in a moment.";
  } else {
    body = "Not yet issued.";
  }
  return <StatusInfoRow label="Contract" body={body} isNarrow={isNarrow} />;
}

function OpenInDocuSignButton({ url }: { url: string }) {
  return (
    <Pressable
      accessibilityRole="link"
      accessibilityLabel="Open in DocuSign"
      onPress={() => {
        if (Platform.OS === "web") window.open(url, "_blank");
      }}
      style={[
        {
          paddingHorizontal: 12,
          paddingVertical: 7,
          borderRadius: 3,
          borderWidth: 1,
          borderColor: "rgba(181,135,58,0.34)",
          backgroundColor: "rgba(181,135,58,0.10)",
        } as ViewStyle,
        Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
      ]}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 10.5,
          letterSpacing: 1.4,
          textTransform: "uppercase",
          color: AMBER,
        }}
      >
        Open in DocuSign
      </Text>
    </Pressable>
  );
}

function formatRelative(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "just now";
  const diff = Date.now() - t;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return `on ${formatDateWithYear(iso)}`;
}

function StatusInfoRow({
  label,
  body,
  isNarrow,
  rhs,
}: {
  label: string;
  body: string;
  isNarrow: boolean;
  rhs?: React.ReactNode;
}) {
  return (
    <View
      style={{
        paddingHorizontal: isNarrow ? 14 : 20,
        paddingVertical: 12,
        flexDirection: isNarrow ? "column" : "row",
        alignItems: isNarrow ? "flex-start" : "center",
        gap: isNarrow ? 6 : 14,
      }}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 9.5,
          letterSpacing: 1.6,
          textTransform: "uppercase",
          color: WL,
          minWidth: 78,
        }}
      >
        {label}
      </Text>
      <Text
        style={{
          flex: isNarrow ? undefined : 1,
          fontFamily: FONTS.body,
          fontSize: 13,
          lineHeight: 19,
          color: WS,
        }}
      >
        {body}
      </Text>
      {rhs ? <View>{rhs}</View> : null}
    </View>
  );
}

/* ─────────────────────────  Payment row  ───────────────────────── */

function PaymentRow({ item, isNarrow }: { item: BillingCase; isNarrow: boolean }) {
  const q = item.quote;
  const p = item.payment;

  if (item.status === "denied" || item.status === "denied_terminal") {
    return <StatusInfoRow label="Payment" body="N/A" isNarrow={isNarrow} />;
  }

  // Drive the row off the invoice row when we have one — that's the
  // authoritative source of truth post-Prompt-6. The case status is the
  // fallback for cases that pre-date an invoice row.
  if (p) {
    if (p.status === "pending") {
      const amount = formatCents(p.total_cents || p.amount_cents);
      return (
        <StatusInfoRow
          label="Payment"
          body={`Initial payment due: ${amount}.`}
          isNarrow={isNarrow}
          rhs={p.checkout_url ? <PayNowButton url={p.checkout_url} label="Pay now" /> : <DisabledPayNowButton />}
        />
      );
    }
    if (p.status === "paid") {
      const body = p.paid_at
        ? `Paid on ${formatDateWithYear(p.paid_at)}.`
        : "Paid.";
      return (
        <StatusInfoRow
          label="Payment"
          body={body}
          isNarrow={isNarrow}
          rhs={p.receipt_url ? <ReceiptLink url={p.receipt_url} /> : undefined}
        />
      );
    }
    if (p.status === "failed") {
      const reason = p.failure_reason ? p.failure_reason : "card declined";
      return (
        <StatusInfoRow
          label="Payment"
          body={`Last payment failed: ${reason}.`}
          isNarrow={isNarrow}
          rhs={p.checkout_url ? <PayNowButton url={p.checkout_url} label="Retry payment" /> : undefined}
        />
      );
    }
    if (p.status === "expired") {
      return (
        <StatusInfoRow
          label="Payment"
          body="Payment link expired. Contact admin@coravu.com to regenerate."
          isNarrow={isNarrow}
        />
      );
    }
    if (p.status === "refunded") {
      return (
        <StatusInfoRow
          label="Payment"
          body="Refunded. Contact admin@coravu.com."
          isNarrow={isNarrow}
        />
      );
    }
    if (p.status === "voided") {
      return <StatusInfoRow label="Payment" body="Cancelled." isNarrow={isNarrow} />;
    }
  }

  // No invoice row yet — fall back to case-status copy.
  if (item.status === "payment_pending") {
    const due = q ? formatCents(q.initial_payment_cents) : "—";
    return (
      <StatusInfoRow
        label="Payment"
        body={`Initial payment due: ${due} — link generating.`}
        isNarrow={isNarrow}
        rhs={<DisabledPayNowButton />}
      />
    );
  }
  if (item.status === "final_invoice_pending") {
    return (
      <StatusInfoRow
        label="Payment"
        body="Final invoice generating — we'll email when ready."
        isNarrow={isNarrow}
      />
    );
  }
  if (item.status === "final_invoice_paid") {
    const paid = item.payment_paid_at
      ? `Paid on ${formatDateWithYear(item.payment_paid_at)}.`
      : "Paid.";
    return <StatusInfoRow label="Payment" body={paid} isNarrow={isNarrow} />;
  }
  if (item.status === "contract_signed") {
    return (
      <StatusInfoRow
        label="Payment"
        body="Not yet due — initial payment link arrives after contract signing is reconciled."
        isNarrow={isNarrow}
      />
    );
  }
  // approved / contract_pending / earlier
  return <StatusInfoRow label="Payment" body="Not yet due." isNarrow={isNarrow} />;
}

function PayNowButton({ url, label }: { url: string; label: string }) {
  return (
    <Pressable
      accessibilityRole="link"
      accessibilityLabel={label}
      onPress={() => {
        if (Platform.OS === "web" && typeof window !== "undefined") {
          window.open(url, "_blank");
        }
      }}
      style={[
        {
          paddingHorizontal: 14,
          paddingVertical: 8,
          borderRadius: 3,
          backgroundColor: AMBER,
          flexDirection: "row",
          alignItems: "center",
          gap: 6,
        } as ViewStyle,
        Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
      ]}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 10.5,
          letterSpacing: 1.6,
          textTransform: "uppercase",
          color: FOREST,
        }}
      >
        {label}
      </Text>
      <ExternalLinkIcon color={FOREST} size={11} />
    </Pressable>
  );
}

function ReceiptLink({ url }: { url: string }) {
  return (
    <Pressable
      accessibilityRole="link"
      accessibilityLabel="View receipt"
      onPress={() => {
        if (Platform.OS === "web" && typeof window !== "undefined") {
          window.open(url, "_blank");
        }
      }}
      style={Platform.OS === "web" ? ({ cursor: "pointer" } as const) : undefined}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyMedium,
          fontSize: 11.5,
          color: AMBER,
        }}
      >
        View receipt →
      </Text>
    </Pressable>
  );
}

function ExternalLinkIcon({ color, size }: { color: string; size: number }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 12 12" fill="none">
      <Path
        d="M4 2H2.5C2.22 2 2 2.22 2 2.5v7c0 .28.22.5.5.5h7c.28 0 .5-.22.5-.5V8"
        stroke={color}
        strokeWidth={1.2}
        strokeLinecap="round"
      />
      <Path d="M6.5 2H10v3.5" stroke={color} strokeWidth={1.2} strokeLinecap="round" />
      <Path d="M5.5 6.5L10 2" stroke={color} strokeWidth={1.2} strokeLinecap="round" />
    </Svg>
  );
}

function DisabledPayNowButton() {
  // Used when payment link is still being generated server-side (or the
  // invoice row hasn't caught up to the case status yet).
  return (
    <Pressable
      disabled
      accessibilityRole="button"
      accessibilityLabel="Pay now"
      accessibilityHint="Payment link not yet available"
      style={[
        {
          paddingHorizontal: 14,
          paddingVertical: 8,
          borderRadius: 3,
          backgroundColor: "rgba(181,135,58,0.18)",
          borderWidth: 1,
          borderColor: "rgba(181,135,58,0.34)",
          opacity: 0.55,
        } as ViewStyle,
        Platform.OS === "web"
          ? ({ cursor: "not-allowed", title: "Payment link not yet available" } as unknown as ViewStyle)
          : null,
      ]}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 10.5,
          letterSpacing: 1.6,
          textTransform: "uppercase",
          color: AMBER,
        }}
      >
        Pay now
      </Text>
    </Pressable>
  );
}

/* ─────────────────────────  Final invoice row  ───────────────────────── */

function FinalInvoiceRow({ item, isNarrow }: { item: BillingCase; isNarrow: boolean }) {
  const fi = item.final_invoice;
  if (!fi) return null;

  const total = formatCents(fi.total_cents);
  const dueDate = fi.due_date ? new Date(fi.due_date) : null;
  const now = Date.now();
  const overdue = fi.status === "pending" && dueDate ? dueDate.getTime() < now : false;
  const daysOverdue = overdue && dueDate
    ? Math.max(1, Math.floor((now - dueDate.getTime()) / 86_400_000))
    : 0;

  let body: string;
  if (fi.status === "paid") {
    body = `Paid on ${fi.paid_at ? formatDateWithYear(fi.paid_at) : "—"}.`;
  } else if (fi.status === "failed") {
    body = `Payment failed${fi.failure_reason ? `: ${fi.failure_reason}` : ""}.`;
  } else if (fi.status === "voided") {
    body = `Invoice voided${fi.void_reason ? ` — ${fi.void_reason}` : ""}. Contact admin@coravu.com.`;
  } else if (overdue) {
    body = `OVERDUE — ${total} was due ${daysOverdue} day${daysOverdue === 1 ? "" : "s"} ago.`;
  } else {
    body = `Final invoice issued — ${total}${dueDate ? ` due ${formatDateWithYear(fi.due_date!)}` : ""}.`;
  }

  function openHosted() {
    if (!fi || !fi.stripe_invoice_hosted_url) return;
    if (Platform.OS === "web" && typeof window !== "undefined") {
      window.open(fi.stripe_invoice_hosted_url, "_blank");
    }
  }
  function openPdf() {
    if (!fi || !fi.stripe_invoice_pdf_url) return;
    if (Platform.OS === "web" && typeof window !== "undefined") {
      window.open(fi.stripe_invoice_pdf_url, "_blank");
    }
  }

  const labelColor = overdue || fi.status === "failed" || fi.status === "voided" ? THEME.destructive : undefined;

  return (
    <View style={{ paddingVertical: 12 }}>
      <View
        style={{
          flexDirection: isNarrow ? "column" : "row",
          gap: isNarrow ? 6 : 14,
          alignItems: isNarrow ? "flex-start" : "center",
        }}
      >
        <Text
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 9.5,
            letterSpacing: 1.6,
            textTransform: "uppercase",
            color: labelColor ?? WL,
            minWidth: isNarrow ? 0 : 120,
          }}
        >
          Final invoice
        </Text>
        <Text
          style={{
            flex: 1,
            fontFamily: FONTS.body,
            fontSize: 13,
            lineHeight: 19,
            color: labelColor ?? LINEN,
          }}
        >
          {body}
        </Text>
      </View>
      {(fi.status === "pending" || fi.status === "failed") && fi.stripe_invoice_hosted_url ? (
        <View style={{ marginTop: 10, flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
          <Pressable
            onPress={openHosted}
            style={[
              {
                paddingHorizontal: 14,
                paddingVertical: 9,
                borderRadius: 4,
                backgroundColor: AMBER,
              },
              Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
            ]}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 11,
                letterSpacing: 1.4,
              textTransform: "uppercase",
              color: THEME.sidebarBg,
            }}
          >
            {overdue || fi.status === "failed" ? "Pay now" : "Pay invoice"}
            </Text>
          </Pressable>
          {fi.stripe_invoice_pdf_url ? (
            <Pressable
              onPress={openPdf}
              style={[
                {
                  paddingHorizontal: 12,
                  paddingVertical: 9,
                  borderRadius: 4,
                  borderWidth: 1,
                  borderColor: BORDER,
                },
                Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
              ]}
            >
              <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11.5, color: WS }}>Download PDF</Text>
            </Pressable>
          ) : null}
        </View>
      ) : fi.status === "paid" && fi.stripe_receipt_url ? (
        <View style={{ marginTop: 8 }}>
          <Pressable
            onPress={() => {
              if (!fi.stripe_receipt_url) return;
              if (Platform.OS === "web" && typeof window !== "undefined") {
                window.open(fi.stripe_receipt_url, "_blank");
              }
            }}
            style={Platform.OS === "web" ? ({ cursor: "pointer" } as const) : undefined}
          >
            <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11.5, color: AMBER }}>
              View receipt →
            </Text>
          </Pressable>
        </View>
      ) : null}
    </View>
  );
}

/* ─────────────────────────  Chips  ───────────────────────── */

function EngagementChip({ label }: { label: string }) {
  return (
    <View
      style={{
        paddingHorizontal: 7,
        paddingVertical: 2.5,
        borderRadius: 999,
        backgroundColor: "rgba(201,162,74,0.16)",
      }}
      accessibilityLabel={`Engagement model: ${label}`}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 9,
          letterSpacing: 1.4,
          textTransform: "uppercase",
          color: COLORS.GOLD_RM.hex,
        }}
      >
        {label}
      </Text>
    </View>
  );
}

function StatusChip({ status }: { status: CompetitionStatus }) {
  const { label, color, bg } = statusChipTokens(status);
  return (
    <View
      style={{
        paddingHorizontal: 7,
        paddingVertical: 2.5,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: color,
        backgroundColor: bg,
      }}
      accessibilityLabel={`Status: ${label}`}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 9,
          letterSpacing: 1.4,
          textTransform: "uppercase",
          color,
        }}
      >
        {label}
      </Text>
    </View>
  );
}

function statusChipTokens(status: CompetitionStatus): {
  label: string;
  color: string;
  bg: string;
} {
  // Amber for in-flight contract/payment phases, sage for done, red for
  // denied/terminal. Matches the color tokens used elsewhere in the
  // portal so the user doesn't have to re-learn the palette.
  switch (status) {
    case "approved":
    case "contract_pending":
    case "payment_pending":
    case "final_invoice_pending":
      return { label: status.replace(/_/g, " "), color: AMBER, bg: "rgba(181,135,58,0.10)" };
    case "contract_signed":
      return { label: "contract signed", color: SAGE, bg: "rgba(143,187,138,0.10)" };
    case "final_invoice_paid":
      return { label: "paid", color: SAGE, bg: "rgba(143,187,138,0.10)" };
    case "denied":
    case "denied_terminal":
      return { label: "denied", color: RED, bg: RED_TINT };
    case "open":
      return { label: "open", color: SAGE, bg: "rgba(143,187,138,0.10)" };
    case "judging":
      return { label: "judging", color: AMBER, bg: "rgba(181,135,58,0.10)" };
    case "closed":
      return { label: "closed", color: WX, bg: "rgba(255,255,255,0.04)" };
    default:
      return { label: status, color: WL, bg: "rgba(255,255,255,0.04)" };
  }
}

/* ─────────────────────────  Glyphs  ───────────────────────── */

function Chevron({ open, color }: { open: boolean; color: string }) {
  const transform = open ? [{ rotate: "180deg" }] : undefined;
  return (
    <View style={{ transform } as unknown as ViewStyle}>
      <Svg width={10} height={10} viewBox="0 0 10 10" fill="none">
        <Path d="M2 3.5L5 6.5L8 3.5" stroke={color} strokeWidth={1.4} strokeLinecap="round" strokeLinejoin="round" />
      </Svg>
    </View>
  );
}

function IconReceipt({ color, size }: { color: string; size: number }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 22 22" fill="none">
      <Path
        d="M5 2h12v18l-2.4-1.5L12.2 20l-2-1.4-2 1.4L6 18.5 3.6 20l-1.6-1.5V2z"
        stroke={color}
        strokeWidth={1.4}
        strokeLinejoin="round"
        transform="translate(2 0)"
      />
      <Path d="M11 6v10" stroke={color} strokeWidth={1.4} strokeLinecap="round" />
      <Path
        d="M13.4 8c-.7-.8-1.6-1.1-2.5-1.1-1.4 0-2.1.7-2.1 1.5 0 2.3 4.7 1.3 4.7 3.8 0 1-1 1.7-2.5 1.7-1.1 0-2-.3-2.6-1.1"
        stroke={color}
        strokeWidth={1.4}
        strokeLinecap="round"
      />
    </Svg>
  );
}

